import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  EnqueueKitchenOrderPrintRequest,
  EnqueuePrintJobRequest,
} from '../../services/cloudPrintJobsApi';
import { clearCredentials, logout } from './authSlice';
import { setCurrentRestaurant } from './restaurantSlice';

/**
 * Everything needed to re-enqueue a batch when the user hits "Reintentar" on the toast.
 * Kept in the store (instead of in the component that fired the print) precisely because the
 * component is usually gone by then — cobro closes its PaymentModal right after printing.
 */
export type PrintRetry =
  | { kind: 'job'; request: EnqueuePrintJobRequest }
  | { kind: 'kitchen'; request: EnqueueKitchenOrderPrintRequest };

export interface PrintBatch {
  /** Stable key: identifies the toast and survives a retry so it updates in place. */
  id: string;
  /** Shown on the toast, e.g. "Ticket de pago" / "Comanda". */
  label: string;
  /** Empty while the enqueue POST is still in flight; filled once the server answers. */
  jobIds: string[];
  retry: PrintRetry;
  /** Restarted on every attempt — drives the poll cadence and the 60 s timeout. */
  startedAt: number;
}

interface CloudPrintState {
  batches: PrintBatch[];
  /**
   * Tenant the batches belong to. A batch carries a full re-enqueue payload, so it must never
   * outlive its session: retrying it after a logout would POST the old restaurant's ticket to
   * whoever is logged in now.
   */
  restaurantId: string | null;
}

const initialState: CloudPrintState = { batches: [], restaurantId: null };

const cloudPrintSlice = createSlice({
  name: 'cloudPrint',
  initialState,
  reducers: {
    /**
     * A print attempt began. Re-firing with an existing id (a retry) resets that batch back to
     * "sending" instead of stacking a second toast for the same ticket.
     */
    printStarted: (
      state,
      action: PayloadAction<{ id: string; label: string; retry: PrintRetry; startedAt: number }>
    ) => {
      const { id, label, retry, startedAt } = action.payload;
      const existing = state.batches.find(b => b.id === id);
      if (existing) {
        existing.label = label;
        existing.retry = retry;
        existing.jobIds = [];
        existing.startedAt = startedAt;
        return;
      }
      state.batches.push({ id, label, retry, jobIds: [], startedAt });
    },
    /** The server accepted the batch and handed back the job ids to watch. */
    printEnqueued: (state, action: PayloadAction<{ id: string; jobIds: string[] }>) => {
      const batch = state.batches.find(b => b.id === action.payload.id);
      if (batch) batch.jobIds = action.payload.jobIds;
    },
    /** Toast closed (auto after success, or by the user). Stops watching and drops the batch. */
    printDismissed: (state, action: PayloadAction<string>) => {
      state.batches = state.batches.filter(b => b.id !== action.payload);
    },
    /** Logout / tenant switch: nothing in flight is ours anymore. */
    printCleared: (state) => {
      state.batches = [];
    },
  },
  extraReducers: (builder) => {
    // Both logout paths: the explicit one and the forced one (expired/invalid token).
    builder
      .addCase(logout.fulfilled, (state) => {
        state.batches = [];
        state.restaurantId = null;
      })
      .addCase(clearCredentials, (state) => {
        state.batches = [];
        state.restaurantId = null;
      })
      // Only on an ACTUAL change of restaurant — this action also fires on a plain re-read of the
      // current tenant, and clearing then would kill a toast mid-print.
      .addCase(setCurrentRestaurant, (state, action) => {
        if (state.restaurantId === action.payload.id) return;
        state.batches = [];
        state.restaurantId = action.payload.id;
      });
  },
});

export const { printStarted, printEnqueued, printDismissed, printCleared } =
  cloudPrintSlice.actions;
export default cloudPrintSlice.reducer;
